<template>
    <v-select
        ref="vueSelectComponent"
        v-model="selectedVariable"
        :clear-search-on-blur="() => blurVariableSelect()"
        :clearable="!variablesOnly"
        :dropdown-should-open="dropDownOpenFunction"
        :loading="loading"
        :options="selectableVariables"
        :placeholder="$t('variables.select')"
        :taggable="!variablesOnly"
        class="d-inline-block select-variable"
        transition=""
        @input="selectVariable"
        @search="search"
    >
        <template #selected-option>
            <span class="select-variable-pre">{{ selectedVariable }}</span>
        </template>
        <template #option="{ label }">
            <span class="select-variable-pre">{{ label }}</span>
        </template>
        <template #list-header>
            <li v-if="!variablesOnly || customDescription" class="p-50 text-center">
                {{
                    customDescription
                        ? $t(`variables.subCategory-description.${customDescription}`)
                        : $t("variables.description")
                }}
            </li>
        </template>
        <template #open-indicator="{ attributes }">
            <span class="variable-open-indication" v-bind="attributes">
                <font-awesome-icon :icon="['fas', 'arrow-down']" />
            </span>
        </template>
    </v-select>
</template>

<script>
import vSelect from "vue-select";
import { defineComponent, computed, ref, nextTick } from "@vue/composition-api";
import useParseMessage from "@/composables/v2.1/useParseMessage";
import LanguageNames from "@/libs/i18nLanguageNames";
import store from "@/store";

export default defineComponent({
    components: {
        vSelect,
    },
    props: {
        exclude: {
            type: Array,
            required: false,
        },
        include: {
            type: Array,
            required: false,
        },
        variablesOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        alwaysOpen: {
            type: Boolean,
            required: false,
            default: false,
        },
        setSearchAfterBlur: {
            type: Boolean,
            required: false,
            default: false,
        },
        value: {
            type: String,
            required: false,
            default: "",
        },
    },
    setup(props, { emit }) {
        const { variables, fetchVariables, loading } = useParseMessage();
        const isVariableSelected = ref(false);

        const vueSelectComponent = ref();

        fetchVariables();

        const subCategories = ref(null);
        const customDescription = ref(null);

        const selectedVariable = computed({
            get: () => props.value,
            set: (newVariable) => {
                // If no variable is present it means it was a searched value
                if (!newVariable.variable) {
                    emit("input", newVariable.label);

                    subCategories.value = null;
                    customDescription.value = null;

                    return;
                }

                // If variable has subcategories show them before showing the old value
                if (newVariable.variable.subCategories) {
                    customDescription.value = newVariable.variable.description;

                    subCategories.value = newVariable.variable.subCategories.map((variable) =>
                        getVariable(variable, `${newVariable.value}:`)
                    );

                    // Focus search again for UX reasons
                    nextTick(() => focusSearch());

                    return;
                }

                // Else its a normal variable selected
                subCategories.value = null;
                customDescription.value = null;

                emit("input", `{{${newVariable.value}}}`);
            },
        });

        const focusSearch = () => {
            vueSelectComponent.value.searchEl.focus();
        };

        const selectVariable = () => {
            isVariableSelected.value = true;
        };

        const search = (newSearchValue) => {
            searchValue.value = newSearchValue;
            isVariableSelected.value = false;
        };

        const searchValue = ref("");

        const getVariable = (variable, prefix) => {
            // If variable is a string return with the prefix attached
            if (typeof variable === "string") {
                const textVariable = `${prefix}${variable}`;

                return {
                    label: textVariable,
                    value: textVariable,
                    variable,
                };
            }

            let label = variable.name;

            // If name start with `translate:` it means a special translation case is present
            if (label.startsWith("translate:")) {
                // @TODO expand variable translation
                const languageToTranslate = /translate:language:(?<language>.+)/.exec(label);

                if (languageToTranslate) {
                    label = LanguageNames.getName(languageToTranslate.groups.language, store.state.appConfig.language);
                }
            } else {
                label = `${prefix}${label}`;
            }

            return {
                label,
                value: `${prefix}${variable.value ?? variable.name}`,
                variable,
            };
        };

        const rootVariables = computed(() => {
            const selectableVariables = props.include
                ? [...props.include.map((variable) => getVariable(variable, ""))]
                : [];

            return [
                ...selectableVariables,
                ...Object.keys(variables.value)
                    .filter((base) => !props.exclude?.includes(base))
                    .map((base) => variables.value[base].map((variable) => getVariable(variable, `${base}.`)))
                    .flat(),
            ];
        });

        const selectableVariables = computed(() => {
            return subCategories.value ?? rootVariables.value;
        });

        const blurVariableSelect = () => {
            if (!searchValue.value || !props.setSearchAfterBlur || isVariableSelected.value) {
                return true;
            }

            selectedVariable.value = { label: searchValue.value };

            return true;
        };

        const isOverwritten = ref(false);

        const dropDownOpenFunction = computed(() =>
            props.alwaysOpen
                ? () => true
                : (vueSelect) => {
                      // Make sure the dropdown does not close when a subcategory is selected
                      if (subCategories.value?.length) {
                          isOverwritten.value = true;
                          return true;
                      }

                      // Reset the component  in case it was overwritten
                      if (isOverwritten.value) {
                          vueSelect.open = false;
                          isOverwritten.value = false;
                      }

                      return vueSelect.open;
                  }
        );

        return {
            subCategories,
            customDescription,
            loading,
            variables,
            selectableVariables,
            selectedVariable,
            blurVariableSelect,
            dropDownOpenFunction,
            searchValue,
            selectVariable,
            search,
            vueSelectComponent,
            focusSearch,
        };
    },
});
</script>

<style lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.select-variable {
    width: $select-variable-min-dropdown-width;

    .variable-open-indication {
        color: $input-placeholder-color;
        cursor: pointer;
    }
}

.vs--open {
    .variable-open-indication {
        margin-top: -0.15rem;
    }
}
</style>
